import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase-config.js';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { initializeConnectionTest } from '../utils/connectionUtils';
import { CircularProgress, Box, Typography, Button } from '@mui/material';
import { getDocument, setDocument } from '../services/databaseService';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase-config.js';
import { resetConnectionState } from '../utils/firestoreConnectionManager';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Loading component
const LoadingScreen = ({ message, onRetry }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    height="100vh"
    gap={2}
  >
    <CircularProgress />
    <Typography variant="body1" color="textSecondary">
      {message || 'Loading...'}
    </Typography>
    {onRetry && (
      <Button 
        variant="contained" 
        color="primary" 
        onClick={onRetry}
        sx={{ mt: 2 }}
      >
        Retry Connection
      </Button>
    )}
  </Box>
);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionStatus, setConnectionStatus] = useState('unknown');
  const [loadingMessage, setLoadingMessage] = useState('Initializing application...');
  
  // Function to manually reset connection
  const handleRetryConnection = async () => {
    setLoadingMessage('Reconnecting to Firestore...');
    try {
      await resetConnectionState();
      setLoadingMessage('Connection reset, continuing initialization...');
      
      // Force re-initialization after a short delay
      setTimeout(() => {
        if (currentUser) {
          // If we have a user, just continue
          setLoading(false);
          setInitialized(true);
        } else {
          // If no user, reload the page to get a fresh start
          window.location.reload();
        }
      }, 2000);
    } catch (error) {
      console.error('Error resetting connection:', error);
      setLoadingMessage('Connection reset failed. Please refresh the page.');
    }
  };

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Setup connection monitoring
  useEffect(() => {
    if (!currentUser) return;
    
    try {
      console.log('Setting up Firestore connection monitoring');
      
      // Initialize connection test document
      initializeConnectionTest(currentUser.uid)
        .then(success => {
          if (!success) {
            console.error('Failed to initialize connection test document');
          }
        })
        .catch(err => {
          console.error('Error in connection test:', err);
        });
      
      // No need for the onSnapshot listener anymore since we're using the proxy
      
    } catch (error) {
      console.error('Error setting up connection monitoring:', error);
    }
  }, [currentUser]);
  
  // Auth state change listener
  useEffect(() => {
    let mounted = true;
    let unsubscribe;

    const initializeAuth = () => {
      try {
        console.log('Initializing Firebase Auth...');
        unsubscribe = onAuthStateChanged(auth, 
          async (user) => {
            if (!mounted) return;
            console.log('Auth state changed:', user ? `User logged in: ${user.uid}` : 'User logged out');
            
            if (user) {
              console.log('User authenticated:', user.uid);
              setCurrentUser(user);
              
              // Reset Firestore connection state after authentication
              try {
                console.log('Resetting Firestore connection state after authentication...');
                await resetConnectionState();
              } catch (resetError) {
                console.error('Error resetting Firestore connection:', resetError);
              }
              
              // Test Firestore access with the authenticated user
              try {
                console.log('Testing Firestore access with authenticated user...');
                const testRef = doc(db, 'test', 'auth-test');
                await setDoc(testRef, {
                  userId: user.uid,
                  timestamp: new Date().toISOString()
                });
                console.log('Successfully wrote to Firestore with authenticated user');
              } catch (firestoreError) {
                console.error('Error writing to Firestore with authenticated user:', firestoreError);
              }
            } else {
              console.log('No user authenticated');
              setCurrentUser(null);
            }
            
            setLoading(false);
            setTimeout(() => {
              setInitialized(true);
            }, 100);
          },
          (error) => {
            console.error('Auth state change error:', error);
            setError(error.message);
            setLoading(false);
            setInitialized(true);
          }
        );
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setError(error.message);
          setLoading(false);
          setInitialized(true);
        }
      }
    };

    initializeAuth();
    
    return () => {
      mounted = false;
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  // Authentication functions
  const handleAuthError = (error) => {
    // Map Firebase auth error codes to user-friendly messages
    const errorMessages = {
      'auth/email-already-in-use': 'This email is already registered. Please use a different email or try logging in.',
      'auth/invalid-email': 'The email address is not valid.',
      'auth/user-disabled': 'This account has been disabled. Please contact support.',
      'auth/user-not-found': 'No account found with this email. Please check your email or sign up.',
      'auth/wrong-password': 'Incorrect password. Please try again or reset your password.',
      'auth/too-many-requests': 'Too many unsuccessful login attempts. Please try again later or reset your password.',
      'auth/network-request-failed': 'Network error. Please check your internet connection and try again.',
      'auth/weak-password': 'Password is too weak. Please use a stronger password with at least 6 characters.',
      'auth/operation-not-allowed': 'This operation is not allowed. Please contact support.',
      'auth/popup-closed-by-user': 'The authentication popup was closed before completing the sign-in process.',
      'auth/unauthorized-domain': 'This domain is not authorized for OAuth operations.',
      'auth/account-exists-with-different-credential': 'An account already exists with the same email but different sign-in credentials.',
    };

    // Get the error message or use a default
    const errorMessage = errorMessages[error.code] || error.message || 'An unknown error occurred';
    
    // Log the error for debugging
    console.error('Authentication error:', error.code, error.message);
    
    return errorMessage;
  };

  const signup = async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Attempting to create user with email:', email);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('User created successfully:', userCredential.user.uid);
      
      // Reset Firestore connection after authentication
      try {
        await resetConnectionState();
      } catch (resetError) {
        console.error('Error resetting connection after signup:', resetError);
      }
      
      // Create initial user document
      try {
        await setDocument('users', userCredential.user.uid, {
          email: userCredential.user.email,
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString()
        });
        console.log('User document created successfully');
      } catch (docError) {
        console.error('Error creating user document:', docError);
        // Continue even if document creation fails
      }
      
      return userCredential.user;
    } catch (error) {
      const errorMessage = handleAuthError(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Attempting login with email:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Login successful:', userCredential.user.uid);
      
      // Reset Firestore connection after authentication
      try {
        await resetConnectionState();
      } catch (resetError) {
        console.error('Error resetting connection after login:', resetError);
      }
      
      // Update last login timestamp
      try {
        await setDocument('users', userCredential.user.uid, {
          lastLogin: new Date().toISOString()
        }, { merge: true });
      } catch (docError) {
        console.error('Error updating last login:', docError);
        // Continue even if update fails
      }
      
      return userCredential.user;
    } catch (error) {
      const errorMessage = handleAuthError(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    return signOut(auth);
  };

  const value = {
    currentUser,
    isOnline,
    connectionStatus,
    error,
    signup,
    login,
    logout
  };

  if (loading) {
    return <LoadingScreen 
      message={loadingMessage} 
      onRetry={initialized ? null : handleRetryConnection} 
    />;
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        p={3}
      >
        <Typography variant="h5" color="error" gutterBottom>
          Authentication Error
        </Typography>
        <Typography variant="body1" paragraph>
          {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
        >
          Reload Application
        </Button>
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
